/**
 * The South African identity number.
 *
 * Thirteen digits, and every one of them says something:
 *
 *     YYMMDD  SSSS  C  A  Z
 *     ------  ----  -  -  -
 *     date of birth
 *             sequence within that day; under 5000 female, 5000 and
 *             above male
 *                   0 for a citizen, 1 for a permanent resident
 *                      a legacy digit, historically a race classification
 *                      under apartheid, now issued as 8 and ignored
 *                         Luhn check digit over the preceding twelve
 *
 * Parsing it properly does real work for onboarding: the date of birth
 * and gender in the number must agree with what the person typed and with
 * what the document scan read, and a mismatch between the three is one of
 * the clearest signals that a document has been altered. The check digit
 * catches transcription errors before anything else is attempted.
 *
 * The legacy digit is read and then discarded. It is not stored, not
 * shown, and not used in any decision - it encodes a racial
 * classification, its retention would be unlawful processing of special
 * personal information under section 26 of POPIA, and there is nothing
 * legitimate to do with it.
 */

export interface SaIdResult {
  valid: boolean;
  reasons: string[];
  dateOfBirth?: string;
  /** Derived, and only ever used to cross-check what the customer told us. */
  gender?: 'male' | 'female';
  citizenship?: 'citizen' | 'permanent_resident';
  age?: number;
}

/** Luhn, as the Department of Home Affairs applies it to the first twelve digits. */
export function luhnCheckDigit(digits: string): number {
  let sum = 0;
  let dbl = true; // The rightmost of the twelve is doubled.
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (dbl) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    dbl = !dbl;
  }
  return (10 - (sum % 10)) % 10;
}

export function validateSaId(input: string, now = Date.now()): SaIdResult {
  const reasons: string[] = [];
  const id = String(input ?? '').replace(/\s/g, '');

  if (!/^\d{13}$/.test(id)) {
    return { valid: false, reasons: ['A South African ID number is exactly thirteen digits.'] };
  }
  if (luhnCheckDigit(id.slice(0, 12)) !== id.charCodeAt(12) - 48) {
    // Almost always a typo rather than a forgery: say so plainly instead
    // of accusing anyone of anything.
    return { valid: false, reasons: ['That ID number does not check out. Please retype it.'] };
  }

  const yy = Number(id.slice(0, 2));
  const mm = Number(id.slice(2, 4));
  const dd = Number(id.slice(4, 6));
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) {
    return { valid: false, reasons: ['The date of birth inside that ID number is not a real date.'] };
  }

  // The century is not in the number. Someone born in 2005 and someone
  // born in 1905 share "05". Resolve towards the living: assume this
  // century if that gives a plausible age, otherwise the last one.
  const sast = new Date(now + 2 * 3_600_000);
  const thisYear = sast.getUTCFullYear();
  let year = Math.floor(thisYear / 100) * 100 + yy;
  if (year > thisYear) year -= 100;

  const dob = new Date(Date.UTC(year, mm - 1, dd));
  if (dob.getUTCFullYear() !== year || dob.getUTCMonth() !== mm - 1 || dob.getUTCDate() !== dd) {
    return { valid: false, reasons: ['The date of birth inside that ID number is not a real date.'] };
  }

  let age = thisYear - year;
  const hadBirthday =
    sast.getUTCMonth() > mm - 1 || (sast.getUTCMonth() === mm - 1 && sast.getUTCDate() >= dd);
  if (!hadBirthday) age -= 1;
  if (age > 120) return { valid: false, reasons: ['That ID number gives an impossible date of birth.'] };
  if (age < 18) reasons.push('The holder of this ID number is under 18.');

  const citizenDigit = id.charCodeAt(10) - 48;
  if (citizenDigit > 1) reasons.push('The citizenship digit in this ID number is not one Home Affairs issues.');

  return {
    valid: true,
    reasons,
    dateOfBirth: `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`,
    gender: Number(id.slice(6, 10)) >= 5000 ? 'male' : 'female',
    citizenship: citizenDigit === 1 ? 'permanent_resident' : 'citizen',
    age,
  };
}

/** Minimum age to hold a wallet in their own name. */
export const MINIMUM_AGE = 18;
